/**
 * The data-source page's binding of the shared detail-page surface.
 *
 * Everything structural - the shell, the drag, the rail, the persistence —
 * lives in `shared/surface`. This file is the whole of what the data-source
 * page adds: its config (`./layout`) and one typed hook over the generic one.
 *
 * It binds the SELECTION-FREE hook: no detail pane, so nothing publishes a
 * `select` that would open a pane it never declared. No reader either - a
 * file still opens in the global side panel.
 */

import {
  useSurfaceWithoutSelection,
  UseDetailSurfaceOptions,
  UseSurfaceWithoutSelectionResult,
} from '../../../shared/surface/useDetailSurface';
import {
  DATA_SOURCE_SURFACE,
  DATA_SOURCE_TABS,
  DataSourcePanelId,
  DataSourceTab,
} from './layout';

export { DATA_SOURCE_SURFACE, DATA_SOURCE_TABS };
export type { DataSourcePanelId, DataSourceTab };

export type DataSourceWorkspace = UseSurfaceWithoutSelectionResult<
  DataSourcePanelId,
  never,
  DataSourceTab
>;

/**
 * The data-source page's layout and tab state.
 *
 * Takes the source's uuid: the surface is entity-scoped, so each data source
 * remembers its own arrangement - see the note at the top of `./layout`.
 */
export const useDataSourceWorkspace = (
  options: UseDetailSurfaceOptions<DataSourceTab> = {}
): DataSourceWorkspace =>
  useSurfaceWithoutSelection<DataSourcePanelId, never, DataSourceTab>(
    DATA_SOURCE_SURFACE,
    options
  );
